import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';

const SIZES = {
  page: 40,
  section: 24,
};

/**
 * The single whole-page / whole-section progress indicator.
 *
 * Every page-blocking loader in the app renders through this component so the
 * boot loader, the route-chunk fallback and a view's own initial fetch all look
 * identical -- one colour, one size per variant, one label treatment. Before
 * this existed the same refresh could show three visually different spinners in
 * a row.
 *
 * - `page`    fills the viewport; used by the boot gate and the lazy-route fallback.
 * - `section` fills its container; used by a view waiting on its own data.
 *
 * The background is set explicitly rather than inherited: an unpainted loader
 * shows the browser's white default, which flashes against the dark theme.
 */
const AppLoader = ({ variant = 'page', label = null, minHeight = null }) => {
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        width: 1,
        height: variant === 'page' ? '100vh' : 1,
        minHeight: minHeight || (variant === 'page' ? '100vh' : 160),
        // Only the page variant paints a background: it renders standalone,
        // before any themed surface exists, so without this it shows the
        // browser's white default and flashes against the dark theme. A section
        // loader sits inside an already-painted container and must stay
        // transparent (it is often a table cell over `background.paper`).
        bgcolor: variant === 'page' ? 'background.default' : 'transparent',
      }}
    >
      <CircularProgress color="primary" size={SIZES[variant] || SIZES.page} />
      {label ? (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      ) : null}
    </Box>
  );
};

AppLoader.propTypes = {
  variant: PropTypes.oneOf(['page', 'section']),
  label: PropTypes.string,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AppLoader;
